import { otherInstalledIntegration } from "@p0security/integrations-installer";

import type { ConnectorCloud } from "./iac.ts";

/** Creates the provider that lists P0's installs of `integration`'s
 * iam-write component: the accounts that a field placing the connector, or
 * its vault, can name. */
const newIamWriteInstallsProvider = (integration: string) =>
  otherInstalledIntegration({ integration, component: "iam-write" });

/** Lists the installs that a field placing the connector, or its vault, can
 * name. */
type InstallOptionProvider = ReturnType<typeof newIamWriteInstallsProvider>;

/** A field naming one of P0's installs, and where P0 lists them. */
type InstallFieldSpec = Readonly<{
  description: string;
  installs: InstallOptionProvider;
}>;

/** The cloud's part in the install: what its connector and vault are called,
 * and where P0 lists the installs that place them. The connector reads the
 * key from its own cloud's secret manager, so one cloud decides both. Only
 * AWS for now: Google Cloud comes in CUS-1059. */
export type CloudDispatch = Readonly<{
  /** The cloud, as the region fields name it */
  label: string;
  /** What the connector runs as */
  runtime: string;
  /** Where to apply the connector's Terraform, or run its shell script, in
   * this cloud */
  deployNote: string;
  regionPlaceholder: string;
  hosting: InstallFieldSpec;
  vault: Readonly<{
    install: InstallFieldSpec;
    /** The secret manager, as its provider names it */
    label: string;
    /** The private key secret field's copy, in the secret manager's words
     * for a secret's name */
    secretDescription: string;
  }>;
}>;

const awsCloud: CloudDispatch = {
  label: "AWS",
  runtime: "Lambda function",
  deployNote:
    "Run either where Docker and the AWS CLI are installed, with the AWS CLI signed in to the connector's account. Both copy P0's connector image into ECR.",
  regionPlaceholder: "us-west-2",
  hosting: {
    description:
      "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
    installs: newIamWriteInstallsProvider("aws"),
  },
  vault: {
    install: {
      description:
        "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
      installs: newIamWriteInstallsProvider("aws-sm"),
    },
    label: "AWS Secrets Manager",
    secretDescription:
      "The name, not the ARN, of the AWS Secrets Manager secret that holds, or will hold, the GitHub App's private key, in the account and region above.",
  },
};

/** Each cloud's part in the install. */
export const CLOUDS: Readonly<Record<ConnectorCloud, CloudDispatch>> = {
  aws: awsCloud,
};
